// mobile_v1: responsive — summary 2x2 + table → cards บน mobile
import React, { useState, useEffect } from 'react';
import API from '../api';

function formatDuration(seconds) {
  if (!seconds || seconds < 0) return '0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h === 0) return `${m}m`;
  return `${h}h ${m}m`;
}

function formatTime(ts) {
  if (!ts) return '-';
  const d = new Date(ts);
  return d.toLocaleString('th-TH', { 
    timeZone: 'Asia/Bangkok',
    hour: '2-digit', minute: '2-digit',
    day: '2-digit', month: 'short'
  });
}

// แปลงวันที่เป็น YYYY-MM-DD ตาม timezone Asia/Bangkok (GMT+7)
function bangkokDateStr(d = new Date()) {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Bangkok',
    year: 'numeric', month: '2-digit', day: '2-digit'
  }).formatToParts(d);
  const obj = Object.fromEntries(parts.map(p => [p.type, p.value]));
  return `${obj.year}-${obj.month}-${obj.day}`;
}

function getDateRange(preset) {
  const todayStr = bangkokDateStr();
  
  if (preset === 'today') {
    return { from: todayStr, to: todayStr };
  }
  if (preset === 'week') {
    const today = new Date();
    const weekStart = new Date(today);
    weekStart.setDate(today.getDate() - today.getDay());
    return { from: bangkokDateStr(weekStart), to: todayStr };
  }
  if (preset === 'month') {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    return { from: bangkokDateStr(monthStart), to: todayStr };
  }
  return { from: todayStr, to: todayStr };
}

export default function TimeReport() {
  const [preset, setPreset] = useState('today');
  const [dates, setDates] = useState(getDateRange('today'));
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const loadReport = async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await API.get('/api/time/report', {
        params: { from: dates.from, to: dates.to }
      });
      setReport(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'โหลดข้อมูลไม่สำเร็จ');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadReport();
    const t = setInterval(loadReport, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [dates]);

  const handlePreset = (p) => {
    setPreset(p);
    setDates(getDateRange(p));
  };

  const handleCustom = (field, value) => {
    setPreset('custom');
    setDates({ ...dates, [field]: value });
  };

  return (
    <div style={{ padding: isMobile ? '16px 12px' : 24, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ marginBottom: isMobile ? 16 : 24 }}>
        <h1 style={{ fontSize: isMobile ? 20 : 24, fontWeight: 700, margin: 0, marginBottom: 4 }}>
          📊 รายงานเวลาทำงาน
        </h1>
        <p style={{ color: '#64748B', fontSize: isMobile ? 12 : 14, margin: 0 }}>
          ติดตามชั่วโมงทำงานของทีม ออนไลน์ตอนนี้ และเวลาทำงานสะสม
        </p>
        {/* timereport_presence_v1 */}
        <p style={{ color: '#94A3B8', fontSize: isMobile ? 11 : 12, margin: '4px 0 0', lineHeight: 1.5 }}>
          <b style={{ color: '#EF9F27' }}>ทำงานจริง</b> = เวลาเปิดแอปทำงานจริง (ตรงกับแท็บ สถานะสด) · <b>ลงชื่อเข้างาน</b> = เวลาที่กดเข้า/ออกงาน
        </p>
      </div>

      {/* Filter bar */}
      <div style={{
        background: '#fff',
        border: '1px solid #E2E8F0',
        borderRadius: 12,
        padding: isMobile ? 12 : 16,
        marginBottom: isMobile ? 12 : 16,
        display: 'flex',
        gap: 8,
        flexWrap: 'wrap',
        alignItems: 'center',
      }}>
        {!isMobile && <span style={{ color: '#64748B', fontSize: 13, marginRight: 8 }}>ช่วงเวลา:</span>}
        {[
          { key: 'today', label: 'วันนี้' },
          { key: 'week', label: 'สัปดาห์' },
          { key: 'month', label: 'เดือน' },
        ].map(p => (
          <button
            key={p.key}
            onClick={() => handlePreset(p.key)}
            style={{
              padding: isMobile ? '8px 14px' : '6px 14px',
              borderRadius: 8,
              border: '1px solid ' + (preset === p.key ? '#EF9F27' : '#E2E8F0'),
              background: preset === p.key ? '#FFF8EC' : '#fff',
              color: preset === p.key ? '#854F0B' : '#1E293B',
              fontSize: isMobile ? 13 : 13,
              fontWeight: 500,
              cursor: 'pointer',
              flex: isMobile ? '1 1 0' : 'none',
              minWidth: isMobile ? 0 : 'auto',
            }}
          >
            {p.label}
          </button>
        ))}
        {/* Date range — แยกบรรทัดบนมือถือ */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          width: isMobile ? '100%' : 'auto',
          flexWrap: 'wrap',
        }}>
          <input
            type="date"
            value={dates.from}
            onChange={e => handleCustom('from', e.target.value)}
            style={{
              padding: isMobile ? '8px 10px' : '6px 10px',
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              fontSize: isMobile ? 14 : 13,
              flex: isMobile ? '1 1 110px' : 'none',
              minWidth: 0,
            }}
          />
          <span style={{ color: '#94A3B8' }}>→</span>
          <input
            type="date"
            value={dates.to}
            onChange={e => handleCustom('to', e.target.value)}
            style={{
              padding: isMobile ? '8px 10px' : '6px 10px',
              border: '1px solid #E2E8F0',
              borderRadius: 8,
              fontSize: isMobile ? 14 : 13,
              flex: isMobile ? '1 1 110px' : 'none',
              minWidth: 0,
            }}
          />
          <button
            onClick={loadReport}
            style={{
              marginLeft: isMobile ? 0 : 'auto',
              padding: isMobile ? '8px 14px' : '6px 14px',
              background: '#1E293B',
              color: '#fff',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 500,
              cursor: 'pointer',
              flexShrink: 0,
            }}
          >
            🔄
          </button>
        </div>
      </div>

      {error && (
        <div style={{
          background: '#FEE2E2',
          border: '1px solid #FCA5A5',
          color: '#991B1B',
          padding: 12,
          borderRadius: 8,
          marginBottom: 16,
          fontSize: 14
        }}>
          ⚠️ {error}
        </div>
      )}

      {loading && !report && (
        <div style={{ textAlign: 'center', padding: 40, color: '#64748B' }}>
          กำลังโหลด...
        </div>
      )}

      {report && (
        <>
          {/* Summary cards — 2x2 บนมือถือ */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? 'repeat(2, 1fr)' : 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: isMobile ? 8 : 12,
            marginBottom: isMobile ? 12 : 16,
          }}>
            <SummaryCard 
              icon="🟢" label="ออนไลน์ตอนนี้"
              value={(report.summary?.online_count ?? 0)}
              suffix="คน"
              color="#10B981"
              isMobile={isMobile}
            />
            <SummaryCard 
              icon="⏱" label="รวมชั่วโมง"
              value={formatDuration((report.summary?.total_seconds ?? 0))}
              color="#EF9F27"
              isMobile={isMobile}
            />
            <SummaryCard 
              icon="📊" label="เฉลี่ยต่อคน"
              value={formatDuration((report.summary?.avg_seconds_per_user ?? 0))}
              color="#3B82F6"
              isMobile={isMobile}
            />
            <SummaryCard 
              icon="🏆" label="Top performer"
              value={report.summary?.top_user?.name || '-'}
              subValue={report.summary?.top_user ? formatDuration(report.summary?.top_user.total_seconds) : null}
              color="#8B5CF6"
              isMobile={isMobile}
            />
          </div>

          {/* Daily chart */}
          {(report.daily?.length ?? 0) > 1 && (
            <div style={{
              background: '#fff',
              border: '1px solid #E2E8F0',
              borderRadius: 12,
              padding: isMobile ? 14 : 20,
              marginBottom: isMobile ? 12 : 16
            }}>
              <h3 style={{ fontSize: isMobile ? 12 : 14, color: '#64748B', textTransform: 'uppercase', letterSpacing: 1, margin: 0, marginBottom: isMobile ? 12 : 16 }}>
                📈 ชั่วโมงทำงานรายวัน
              </h3>
              <DailyChart data={report.daily} isMobile={isMobile} />
            </div>
          )}

          {/* User list — Cards บน mobile, Table บน desktop */}
          <div style={{
            background: '#fff',
            border: '1px solid #E2E8F0',
            borderRadius: 12,
            overflowX: 'auto' /* timereport_presence_v1 */
          }}>
            {(report.users?.length ?? 0) === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#94A3B8' }}>
                ไม่มีข้อมูลในช่วงเวลานี้
              </div>
            ) : isMobile ? (
              /* mobile: Card list */
              <div>
                {(report.users ?? []).map((u, i) => (
                  <UserCard key={u.user_id} user={u} isLast={i === report.users.length - 1} />
                ))}
              </div>
            ) : (
              /* desktop: ตารางเดิม */
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#F8FAFC' }}>
                    <th style={th}>สถานะ</th>
                    <th style={th}>ชื่อ</th>
                    <th style={th}>บทบาท</th>
                    <th style={{...th, textAlign: 'right'}}>⏱ ทำงานจริง</th>
                    <th style={{...th, textAlign: 'right'}}>ลงชื่อเข้างาน</th>
                    <th style={{...th, textAlign: 'right'}}>Sessions</th>
                    <th style={th}>🟢 เข้าล่าสุด</th>
                    <th style={th}>🔴 ออกล่าสุด</th>
                    <th style={th}>เห็นล่าสุด</th>
                  </tr>
                </thead>
                <tbody>
                  {(report.users ?? []).map(u => (
                    <tr key={u.user_id} style={{ borderTop: '1px solid #E2E8F0' }}>
                      <td style={td}>
                        <span style={{
                          display: 'inline-block',
                          width: 8, height: 8, borderRadius: '50%',
                          background: u.is_online ? '#10B981' : '#94A3B8',
                          marginRight: 6
                        }}></span>
                        {u.is_online ? 'Online' : 'Offline'}
                      </td>
                      <td style={{...td, fontWeight: 500}}>{u.name}</td>
                      <td style={td}>
                        <span style={{
                          padding: '2px 8px', borderRadius: 999, fontSize: 11, fontWeight: 500,
                          background: u.role === 'tenant' ? '#FEF3C7' : '#DBEAFE',
                          color: u.role === 'tenant' ? '#854F0B' : '#1E40AF'
                        }}>
                          {u.role === 'tenant' ? 'เจ้าของ' : 'พนักงาน'}
                        </span>
                      </td>
                      <td style={{...td, textAlign: 'right', fontWeight: 700, color: u.worked_seconds > 0 ? '#1E293B' : '#94A3B8'}}>
                        {formatDuration(u.worked_seconds)}
                      </td>
                      <td style={{...td, textAlign: 'right', fontWeight: 500, color: u.total_seconds > 0 ? '#64748B' : '#CBD5E1'}}>
                        {formatDuration(u.total_seconds)}
                      </td>
                      <td style={{...td, textAlign: 'right', color: '#64748B'}}>{u.sessions}</td>
                      <td style={{...td, fontSize: 12, color: u.last_clock_in ? '#16A34A' : '#94A3B8'}}>{formatTime(u.last_clock_in)}</td>
                      <td style={{...td, fontSize: 12, color: u.last_clock_out ? '#DC2626' : '#94A3B8'}}>{formatTime(u.last_clock_out)}</td>
                      <td style={{...td, fontSize: 12, color: '#64748B'}}>{formatTime(u.last_heartbeat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
}

const th = {
  padding: '12px 16px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: '#64748B',
  textTransform: 'uppercase',
  letterSpacing: 0.5
};

const td = {
  padding: '12px 16px',
  fontSize: 14
};

function SummaryCard({ icon, label, value, subValue, suffix, color, isMobile }) {
  return (
    <div style={{
      background: '#fff',
      border: '1px solid #E2E8F0',
      borderRadius: 12,
      padding: isMobile ? 12 : 16,
      display: 'flex',
      flexDirection: 'column',
      gap: isMobile ? 4 : 6,
      minWidth: 0,
    }}>
      <div style={{
        fontSize: isMobile ? 11 : 12,
        color: '#64748B',
        display: 'flex', alignItems: 'center', gap: 6,
        whiteSpace: 'nowrap',
        overflow: 'hidden', textOverflow: 'ellipsis',
      }}>
        <span>{icon}</span>
        <span>{label}</span>
      </div>
      <div style={{
        fontSize: isMobile ? 20 : 24,
        fontWeight: 700,
        color: color || '#1E293B',
        lineHeight: 1.1,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
      }}>
        {value}{suffix && <span style={{ fontSize: isMobile ? 12 : 14, fontWeight: 400, marginLeft: 4 }}>{suffix}</span>}
      </div>
      {subValue && <div style={{ fontSize: isMobile ? 11 : 12, color: '#94A3B8' }}>{subValue}</div>}
    </div>
  );
}

// mobile_v1: User card สำหรับมือถือ
function UserCard({ user, isLast }) {
  return (
    <div style={{
      padding: 14,
      borderBottom: isLast ? 'none' : '1px solid #E2E8F0',
    }}>
      {/* Top: สถานะ + ชื่อ + ชั่วโมง */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 12, height: 12, borderRadius: '50%',
          background: user.is_online ? '#10B981' : '#94A3B8',
          flexShrink: 0,
        }} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 15, color: '#1E293B', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {user.name}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 2 }}>
            <span style={{
              padding: '2px 8px', borderRadius: 999, fontSize: 10, fontWeight: 500,
              background: user.role === 'tenant' ? '#FEF3C7' : '#DBEAFE',
              color: user.role === 'tenant' ? '#854F0B' : '#1E40AF',
            }}>
              {user.role === 'tenant' ? 'เจ้าของ' : 'พนักงาน'}
            </span>
            <span style={{ fontSize: 11, color: user.is_online ? '#10B981' : '#94A3B8', fontWeight: 500 }}>
              {user.is_online ? 'Online' : 'Offline'}
            </span>
          </div>
        </div>
        <div style={{ textAlign: 'right', flexShrink: 0 }}>
          <div style={{
            fontWeight: 700, fontSize: 18,
            color: user.worked_seconds > 0 ? '#EF9F27' : '#94A3B8',
            lineHeight: 1.1,
          }}>
            {formatDuration(user.worked_seconds)}
          </div>
          <div style={{ fontSize: 10, color: '#94A3B8', marginTop: 1 }}>ทำงานจริง</div>
          <div style={{ fontSize: 11, color: '#94A3B8', marginTop: 3 }}>
            ลงชื่อ {formatDuration(user.total_seconds)} · {user.sessions} ครั้ง
          </div>
        </div>
      </div>

      {/* Stats: เข้าล่าสุด / ออกล่าสุด / เห็นล่าสุด */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 6 }}>
        <div style={{ background: '#F0FDF4', padding: '6px 8px', borderRadius: 6 }}>
          <div style={{ fontSize: 9, color: '#16A34A', fontWeight: 600, marginBottom: 1, textTransform: 'uppercase' }}>🟢 เข้า</div>
          <div style={{ fontSize: 11, color: user.last_clock_in ? '#16A34A' : '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {formatTime(user.last_clock_in)}
          </div>
        </div>
        <div style={{ background: '#FEF2F2', padding: '6px 8px', borderRadius: 6 }}>
          <div style={{ fontSize: 9, color: '#DC2626', fontWeight: 600, marginBottom: 1, textTransform: 'uppercase' }}>🔴 ออก</div>
          <div style={{ fontSize: 11, color: user.last_clock_out ? '#DC2626' : '#94A3B8', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {formatTime(user.last_clock_out)}
          </div>
        </div>
        <div style={{ background: '#F8FAFC', padding: '6px 8px', borderRadius: 6 }}>
          <div style={{ fontSize: 9, color: '#64748B', fontWeight: 600, marginBottom: 1, textTransform: 'uppercase' }}>เห็น</div>
          <div style={{ fontSize: 11, color: '#64748B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {formatTime(user.last_heartbeat)}
          </div>
        </div>
      </div>
    </div>
  );
}

function DailyChart({ data, isMobile }) {
  const maxSeconds = Math.max(...data.map(d => d.seconds), 1);
  return (
    <div style={{
      display: 'flex',
      alignItems: 'flex-end',
      gap: isMobile ? 4 : 8,
      height: isMobile ? 100 : 120,
      paddingTop: 20,
      overflowX: isMobile ? 'auto' : 'visible',
    }}>
      {data.map(d => {
        const height = (d.seconds / maxSeconds) * 100;
        const date = new Date(d.date);
        return (
          <div key={d.date} style={{
            flex: isMobile ? '0 0 auto' : 1,
            minWidth: isMobile ? 38 : 'auto',
            display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
          }}>
            <div style={{ fontSize: isMobile ? 9 : 11, color: '#64748B', fontWeight: 500, whiteSpace: 'nowrap' }}>
              {formatDuration(d.seconds)}
            </div>
            <div style={{
              width: '100%',
              height: `${Math.max(height, 4)}%`,
              background: 'linear-gradient(180deg, #EF9F27 0%, #BA7517 100%)',
              borderRadius: '4px 4px 0 0',
              minHeight: 4
            }}></div>
            <div style={{ fontSize: isMobile ? 9 : 11, color: '#94A3B8', whiteSpace: 'nowrap' }}>
              {date.toLocaleDateString('th-TH', { day: 'numeric', month: 'short' })}
            </div>
          </div>
        );
      })}
    </div>
  );
}
